import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  FaArrowLeft, FaSearch, FaFilter, FaDownload, FaEye,
  FaPlus, FaMinus, FaBarcode, FaCalendarAlt, FaUser, FaWarehouse,
  FaTimes, FaQrcode, FaBox, FaWineBottle, FaTrash, FaExclamationTriangle
} from 'react-icons/fa';
import api from '../../utils/api';
import Loader from '../../components/ui/Loader';
import Message from '../../components/ui/Message';
import Paginate from '../../components/ui/Paginate';
import { deleteInboundRecord, deleteOutboundRecord } from '../../actions/inventoryActions';
import FilterProductSelector from '../../components/inventory/FilterProductSelector';
import OutboundBarcodeScanner from '../../components/inventory/OutboundBarcodeScanner';
import InboundBarcodeDetails from '../../components/inventory/InboundBarcodeDetails';
import OutboundBarcodeDetails from '../../components/inventory/OutboundBarcodeDetails';

const TransactionHistoryinvPage = () => {
  const { warehouseId } = useParams();
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const isSuperAdmin = user?.is_super_admin || false;
  const [activeTab, setActiveTab] = useState('all');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    product_id: '',
    warehouse_id: warehouseId || '',
    operator: '',
    start_date: '',
    end_date: '',
    search_code: ''
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);

  useEffect(() => {
    fetchTransactions();
    fetchProducts();
    fetchWarehouses();
  }, [activeTab, filters, pagination.page]);

  const fetchTransactions = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        ...filters,
        type: activeTab === 'all' ? '' : activeTab,
        page: pagination.page,
        limit: pagination.limit
      });
      
      const response = await api.get(`/inventory/transactions/history?${params}`);
      
      // Add defensive programming to handle missing pagination
      const responseData = response.data || {};
      const transactions = responseData.transactions || [];
      const paginationData = responseData.pagination || {
        total: transactions.length,
        page: 1,
        limit: 20,
        totalPages: Math.ceil(transactions.length / 20)
      };
      
      setTransactions(transactions);
      setPagination(prev => ({
        ...prev,
        total: paginationData.total || 0,
        totalPages: paginationData.totalPages || 0,
        page: paginationData.page || 1
      }));
    } catch (err) {
      console.error('API Error:', err);
      setError(err.response?.data?.error || '获取交易记录失败');
      // Set empty data on error
      setTransactions([]);
      setPagination(prev => ({
        ...prev,
        total: 0,
        totalPages: 0
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products?limit=1000');
      const responseData = response.data || {};
      setProducts(responseData.products || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await api.get('/inventory/warehouses');
      setWarehouses(response.data || []);
    } catch (err) {
      console.error('Error fetching warehouses:', err);
      setWarehouses([]);
    }
  };

  const handleDeleteTransaction = async (transaction) => {
    const isInbound = transaction.transaction_type === 'inbound';
    const recordType = isInbound ? '入库记录' : '出库记录';
    
    if (window.confirm(`确定要删除这条${recordType}吗？此操作无法撤销。`)) {
      try {
        if (isInbound) {
          await dispatch(deleteInboundRecord(transaction.id));
        } else {
          await dispatch(deleteOutboundRecord(transaction.id));
        }
        alert(`${recordType}删除成功`);
        fetchTransactions(); // 刷新交易记录
      } catch (error) {
        alert(`删除失败：${error.error || error.message}`);
      }
    }
  };

  const handleSearch = async (searchCode) => {
    if (!searchCode.trim()) return;
    // 处理URL格式的二维码
    const extractCodeFromUrl = (inputCode) => {
      try {
        if (inputCode.toLowerCase().startsWith('http')) {
          const url = new URL(inputCode);
          const codeFromX = url.searchParams.get('X');
          const codeFromCode = url.searchParams.get('code');
          const codeFromId = url.searchParams.get('id');
          
          if (codeFromX) return codeFromX;
          if (codeFromCode) return codeFromCode;
          if (codeFromId) return codeFromId;
          
          const pathParts = url.pathname.split('/').filter(part => part);
          if (pathParts.length > 0) {
            return pathParts[pathParts.length - 1];
          }
        }
        return inputCode;
      } catch (error) {
        console.warn('Failed to parse URL, using original code:', error);
        return inputCode;
      }
    };
 
    const actualSearchCode = extractCodeFromUrl(searchCode);
    console.log('Original search code:', searchCode);
    console.log('Extracted search code:', actualSearchCode);

    setLoading(true);
    try {
      // 先尝试瓶码搜索 - 添加 include_sold=true 参数
      try {
        const unitResponse = await api.get(`/inventory/search/unit/${actualSearchCode}?include_sold=true`);
        let unitData = unitResponse.data;
        
        // 如果是已售商品但没有出库信息，尝试通过交易记录获取
        if (unitData.unit.status === 'sold' && !unitData.outbound) {
          try {
            const transactionResponse = await api.get(`/inventory/transactions/history?type=outbound&product_id=${unitData.product.id}&warehouse_id=${unitData.warehouse.id}&limit=10`);
            if (transactionResponse.data.transactions && transactionResponse.data.transactions.length > 0) {
              // 找到最近的出库记录
              const latestOutbound = transactionResponse.data.transactions.sort((a, b) => 
                new Date(b.created_at) - new Date(a.created_at)
              )[0];
              
              unitData.outbound = {
                id: latestOutbound.id,
                customer: latestOutbound.partner,
                unit_price: latestOutbound.unit_price,
                created_at: latestOutbound.created_at,
                operator: latestOutbound.operator,
                notes: latestOutbound.notes
              };
            }
          } catch (transactionError) {
            console.warn('Failed to fetch outbound transaction details:', transactionError);
          }
        }
        
        setSearchResult({ type: 'unit', data: unitData });
        setShowSearchModal(true);
        return;
      } catch (unitError) {
        // 如果瓶码搜索失败，尝试箱码搜索 - 也添加 include_sold=true 参数
        try {
          const boxResponse = await api.get(`/inventory/search/box/${actualSearchCode}?include_sold=true`);
          let boxData = boxResponse.data;
          
          // 如果箱子中有已售商品但缺少出库信息，尝试补充
          if (boxData.soldUnits && boxData.soldUnits.length > 0) {
            try {
              const transactionResponse = await api.get(`/inventory/transactions/history?type=outbound&product_id=${boxData.product.id}&warehouse_id=${boxData.warehouse.id}&limit=50`);
              
              if (transactionResponse.data.transactions && transactionResponse.data.transactions.length > 0) {
                // 按时间排序，找到最相关的出库记录
                const outboundTransactions = transactionResponse.data.transactions.sort((a, b) => 
                  new Date(b.created_at) - new Date(a.created_at)
                );
                
                // 为缺少出库信息的已售商品补充信息
                const latestOutbound = outboundTransactions[0];
                
                boxData.soldUnits.forEach(unit => {
                  if (!unit.customer || !unit.unit_price) {
                    unit.customer = unit.customer || latestOutbound.partner;
                    unit.unit_price = unit.unit_price || latestOutbound.unit_price;
                    unit.outbound_date = unit.outbound_date || latestOutbound.created_at;
                  }
                });
              }
            } catch (transactionError) {
              console.warn('Failed to fetch outbound transactions:', transactionError);
            }
          }
          
          setSearchResult({ type: 'box', data: boxData });
          setShowSearchModal(true);
          return;
        } catch (boxError) {
          setError('未找到匹配的条码记录');
        }
      }
    } catch (err) {
      setError('搜索失败');
    } finally {
      setLoading(false);
    }
  };

  const handleScanComplete = (scannedCode) => {
    setShowScanner(false);
    handleSearch(scannedCode);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('zh-CN');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount || 0);
  };

  const getTransactionIcon = (type) => {
    return type === 'inbound' ? <FaPlus className="text-green-600" /> : <FaMinus className="text-red-600" />;
  };

  const getTransactionColor = (type) => {
    return type === 'inbound' ? 'text-green-600' : 'text-red-600';
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  return (
    <div className="transaction-history-page">
      {/* 页面头部 - 移动端优化 */}
      <div className="mb-6">
        <Link
          to="/admin/inventory"
          className="inline-flex items-center text-gray-600 hover:text-red-700 mb-4"
        >
          <FaArrowLeft className="mr-2" />
          返回库存管理
        </Link>
        
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className="text-2xl md:text-3xl font-bold">交易记录</h1>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex-1 sm:flex-none bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center justify-center text-sm md:text-base"
            >
              <FaFilter className="mr-2" />
              筛选
            </button>
            <button className="flex-1 sm:flex-none bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center text-sm md:text-base">
              <FaDownload className="mr-2" />
              导出
            </button>
          </div>
        </div>
      </div>

      {/* 搜索栏 - 移动端优化 */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <div className="relative">
                <FaBarcode className="absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="输入瓶码或箱码搜索..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch(e.target.value);
                    }
                  }}
                />
              </div>
            </div>
            <button
              onClick={() => setShowScanner(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center text-sm md:text-base"
              title="扫码搜索"
            >
              <FaQrcode className="md:mr-2" />
              <span className="hidden md:inline">扫码</span>
            </button>
          </div>
          <button
            onClick={() => {
              const input = document.querySelector('input[placeholder="输入瓶码或箱码搜索..."]');
              handleSearch(input.value);
            }}
            className="w-full bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center text-sm md:text-base"
          >
            <FaSearch className="mr-2" />
            搜索
          </button>
        </div>
      </div>

      {/* 筛选器 - 移动端优化 */}
      {showFilters && (
        <div className="border-b bg-gray-50 p-4">
          <div className="space-y-4">
            {/* 日期筛选 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  开始时间
                </label>
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                  className="w-full p-2 border rounded text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  结束时间
                </label>
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                  className="w-full p-2 border rounded text-sm"
                />
              </div>
            </div>
            
            {/* 产品和仓库筛选 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  产品
                </label>
                <FilterProductSelector
                  value={filters.product_id}
                  onChange={(value) => setFilters(prev => ({ ...prev, product_id: value || '' }))}
                  placeholder="全部产品"
                  maxDisplayItems={10}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  仓库
                </label>
                <select
                  value={filters.warehouse_id}
                  onChange={(e) => setFilters(prev => ({ ...prev, warehouse_id: e.target.value }))}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="">全部仓库</option>
                  {warehouses.map(warehouse => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* 操作人和重置按钮 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  操作人
                </label>
                <input
                  type="text"
                  value={filters.operator}
                  onChange={(e) => setFilters(prev => ({ ...prev, operator: e.target.value }))}
                  placeholder="搜索操作人"
                  className="w-full p-2 border rounded text-sm"
                />
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setFilters({
                      type: '',
                      product_id: '',
                      warehouse_id: warehouseId || '',
                      operator: '',
                      start_date: '',
                      end_date: '',
                      search_code: ''
                    });
                  }}
                  className="w-full bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 text-sm"
                >
                  重置
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="flex border-b overflow-x-auto">
          <button
            onClick={() => setActiveTab('all')}
            className={`flex-shrink-0 px-4 md:px-6 py-3 font-medium text-sm md:text-base ${
              activeTab === 'all' 
                ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📋 全部记录
          </button>
          <button
            onClick={() => setActiveTab('inbound')}
            className={`flex-shrink-0 px-4 md:px-6 py-3 font-medium text-sm md:text-base ${
              activeTab === 'inbound' 
                ? 'bg-green-50 text-green-600 border-b-2 border-green-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📥 入库记录
          </button>
          <button
            onClick={() => setActiveTab('outbound')}
            className={`flex-shrink-0 px-4 md:px-6 py-3 font-medium text-sm md:text-base ${
              activeTab === 'outbound' 
                ? 'bg-red-50 text-red-600 border-b-2 border-red-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📤 出库记录
          </button>
        </div>

        {/* 交易记录内容 - 桌面端表格 + 移动端卡片 */}
        {loading ? (
          <div className="p-8">
            <Loader />
          </div>
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-500">暂无交易记录</div>
          </div>
        ) : (
          <>
            {/* 桌面端表格视图 */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      时间
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      类型
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      产品
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      仓库
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      数量
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      单价
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      总金额
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      合作方
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作人
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((transaction) => (
                    <tr key={`${transaction.transaction_type}-${transaction.id}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(transaction.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`flex items-center ${getTransactionColor(transaction.transaction_type)}`}>
                          {getTransactionIcon(transaction.transaction_type)}
                          <span className="ml-2 font-medium">
                            {transaction.transaction_type === 'inbound' ? '入库' : '出库'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {transaction.product_name}
                        </div>
                        {transaction.chinese_name && (
                          <div className="text-sm text-gray-500">
                            {transaction.chinese_name}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.warehouse_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <span className={getTransactionColor(transaction.transaction_type)}>
                          {transaction.transaction_type === 'inbound' ? '+' : '-'}{transaction.quantity} 瓶
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(transaction.unit_price)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        {formatCurrency(transaction.total_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.partner || transaction.customer || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.operator || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => {
                            setSelectedTransaction(transaction);
                            setShowDetailModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                          title="查看详情"
                        >
                          <FaEye />
                        </button>
                        {isSuperAdmin && (
                          <button
                            onClick={() => handleDeleteTransaction(transaction)}
                            className="text-red-600 hover:text-red-900 ml-3 p-1 rounded hover:bg-red-50 transition-colors"
                            title="删除记录"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 移动端卡片视图 */}
            <div className="lg:hidden">
              {transactions.map((transaction) => (
                <div 
                  key={`${transaction.transaction_type}-${transaction.id}`} 
                  className="border-b border-gray-200 last:border-b-0 p-4 hover:bg-gray-50"
                >
                  <div className="space-y-3">
                    {/* 卡片头部 */}
                    <div className="flex justify-between items-start">
                      <div className="flex items-center">
                        <div className={`flex items-center ${getTransactionColor(transaction.transaction_type)} mr-3`}>
                          {getTransactionIcon(transaction.transaction_type)}
                          <span className="ml-1 font-medium text-sm">
                            {transaction.transaction_type === 'inbound' ? '入库' : '出库'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDate(transaction.created_at)}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedTransaction(transaction);
                            setShowDetailModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50 transition-colors"
                          title="查看详情"
                        >
                          <FaEye size={14} />
                        </button>
                        {isSuperAdmin && (
                          <button
                            onClick={() => handleDeleteTransaction(transaction)}
                            className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50 transition-colors"
                            title="删除记录"
                          >
                            <FaTrash size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* 产品信息 */}
                    <div>
                      <div className="font-medium text-gray-900 text-sm">
                        {transaction.product_name}
                      </div>
                      {transaction.chinese_name && (
                        <div className="text-xs text-gray-500">
                          {transaction.chinese_name}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        📍 {transaction.warehouse_name}
                      </div>
                    </div>
                    
                    {/* 数量和金额信息 */}
                    <div className="grid grid-cols-3 gap-4 text-center bg-gray-50 rounded-lg p-3">
                      <div>
                        <div className={`text-sm font-bold ${getTransactionColor(transaction.transaction_type)}`}>
                          {transaction.transaction_type === 'inbound' ? '+' : '-'}{transaction.quantity}
                        </div>
                        <div className="text-xs text-gray-500">数量(瓶)</div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-gray-900">
                          {formatCurrency(transaction.unit_price)}
                        </div>
                        <div className="text-xs text-gray-500">单价</div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-gray-900">
                          {formatCurrency(transaction.total_amount)}
                        </div>
                        <div className="text-xs text-gray-500">总金额</div>
                      </div>
                    </div>
                    
                    {/* 合作方和操作人 */}
                    <div className="flex justify-between text-xs">
                      <div>
                        <span className="text-gray-500">
                          {transaction.transaction_type === 'inbound' ? '供应商:' : '客户:'}
                        </span>
                        <span className="ml-1 text-gray-700">
                          {transaction.partner || transaction.customer || '-'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">操作:</span>
                        <span className="ml-1 text-gray-700">
                          {transaction.operator || '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* 分页 - 移动端优化 */}
        {pagination.totalPages > 1 && (
          <div className="px-4 md:px-6 py-4 border-t">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs md:text-sm text-gray-500 text-center sm:text-left">
                共 {pagination.total} 条记录，第 {pagination.page} 页，共 {pagination.totalPages} 页
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1 border rounded text-xs md:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  上一页
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-1 border rounded text-xs md:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  下一页
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 搜索结果模态框 - 移动端优化 */}
      {showSearchModal && searchResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-white border-b px-4 md:px-6 py-4 flex justify-between items-center">
              <h2 className="text-lg md:text-2xl font-bold">
                {searchResult.type === 'unit' ? '🍷 瓶码信息' : '📦 箱码信息'}
              </h2>
              <button 
                onClick={() => setShowSearchModal(false)}
                className="text-gray-500 hover:text-gray-700 p-2"
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              {searchResult.type === 'unit' ? (
                <UnitSearchResult data={searchResult.data} />
              ) : (
                <BoxSearchResult data={searchResult.data} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* 交易详情模态框 - 移动端优化 */}
      {showDetailModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="flex-shrink-0 bg-white border-b px-4 md:px-6 py-3 md:py-4 flex justify-between items-center">
              <h2 className="text-lg md:text-2xl font-bold">
                {selectedTransaction.transaction_type === 'inbound' ? '📦 入库详情' : '📤 出库详情'}
              </h2>
              <button 
                onClick={() => setShowDetailModal(false)}
                className="text-gray-500 hover:text-gray-700 p-2"
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 md:p-6">
                {/* 基本信息 - 移动端栅格优化 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 text-sm">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">交易类型</label>
                    <div className={`flex items-center mt-1 ${getTransactionColor(selectedTransaction.transaction_type)}`}>
                      {getTransactionIcon(selectedTransaction.transaction_type)}
                      <span className="ml-2 font-medium">
                        {selectedTransaction.transaction_type === 'inbound' ? '入库' : '出库'}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700">交易时间</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedTransaction.created_at)}</p>
                  </div>
                  
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-700">产品名称</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedTransaction.product_name}</p>
                    {selectedTransaction.chinese_name && (
                      <p className="text-xs text-gray-500">{selectedTransaction.chinese_name}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700">仓库</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedTransaction.warehouse_name}</p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700">数量</label>
                    <p className={`mt-1 text-sm font-medium ${getTransactionColor(selectedTransaction.transaction_type)}`}>
                      {selectedTransaction.transaction_type === 'inbound' ? '+' : '-'}{selectedTransaction.quantity} 瓶
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700">单价</label>
                    <p className="mt-1 text-sm text-gray-900">{formatCurrency(selectedTransaction.unit_price)}</p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700">总金额</label>
                    <p className="mt-1 text-sm text-gray-900 font-medium">{formatCurrency(selectedTransaction.total_amount)}</p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      {selectedTransaction.transaction_type === 'inbound' ? '供应商' : '客户'}
                    </label>
                    <p className="mt-1 text-sm text-gray-900">{selectedTransaction.partner || '-'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700">操作人</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedTransaction.operator || '-'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700">记录ID</label>
                    <p className="mt-1 text-xs text-gray-500">#{selectedTransaction.id}</p>
                  </div>
                </div>
                
                {selectedTransaction.notes && (
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-gray-700">备注</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedTransaction.notes}</p>
                  </div>
                )}
              </div>
              
              {/* 条码详情展示 */}
              <div className="mt-6 border-t pt-6 px-4 md:px-6">
                <h3 className="text-base md:text-lg font-semibold mb-4 flex items-center">
                  <FaBarcode className="mr-2 text-blue-600" />
                  {selectedTransaction.transaction_type === 'inbound' ? '入库' : '出库'}条码详情
                </h3>
                
                {selectedTransaction.transaction_type === 'inbound' ? (
                  <InboundBarcodeDetails inboundId={selectedTransaction.id} />
                ) : (
                  <OutboundBarcodeDetails outboundId={selectedTransaction.id} />
                )}
              </div>
            </div>
            
            <div className="flex-shrink-0 px-4 md:px-6 py-3 md:py-4 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => setShowDetailModal(false)}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm md:text-base"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 扫码器 */}
      {showScanner && (
        <OutboundBarcodeScanner
          onComplete={handleScanComplete}
          onClose={() => setShowScanner(false)}
          title="扫描瓶码或箱码"
        />
      )}
    </div>
  );
};

const UnitSearchResult = ({ data }) => {
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('zh-CN');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6">
      {/* 瓶子基本信息 */}
      <div className={`${data.unit.status === 'available' ? 'bg-blue-50' : 'bg-red-50'} rounded-lg p-4`}>
        <h3 className="text-lg font-semibold mb-3 flex items-center">
          <FaWineBottle className={`mr-2 ${data.unit.status === 'available' ? 'text-blue-600' : 'text-red-600'}`} />
          瓶子信息
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">瓶条码：</span>
            <span className="font-mono">{data.unit.unit_barcode || '无'}</span>
          </div>
          <div>
            <span className="text-gray-600">瓶二维码：</span>
            <span className="font-mono">{data.unit.unit_qrcode || '无'}</span>
          </div>
          <div>
            <span className="text-gray-600">状态：</span>
            <span className={`font-medium ${data.unit.status === 'available' ? 'text-green-600' : 'text-red-600'}`}>
              {data.unit.status === 'available' ? '可售' : '已售'}
            </span>
          </div>
          <div>
            <span className="text-gray-600">瓶位置：</span>
            <span>第{data.box.index}箱 第{data.unit.index}瓶</span>
          </div>
          {data.unit.sold_at && (
            <div className="col-span-2">
              <span className="text-gray-600">售出时间：</span>
              <span>{formatDate(data.unit.sold_at)}</span>
            </div>
          )}
        </div>
      </div>

      {/* 产品信息 */}
      <div className="bg-green-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-3">产品信息</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">产品名称：</span>
            <span className="font-medium">{data.product.name}</span>
          </div>
          {data.product.chinese_name && (
            <div>
              <span className="text-gray-600">中文名称：</span>
              <span>{data.product.chinese_name}</span>
            </div>
          )}
          <div>
            <span className="text-gray-600">仓库：</span>
            <span>{data.warehouse.name}</span>
          </div>
        </div>
      </div>

      {/* 入库信息 */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-3">入库信息</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">入库成本：</span>
            <span>{formatCurrency(data.inbound.unit_cost)}/瓶</span>
          </div>
        </div>
      </div>

      {/* 出库信息 - 新增：显示已售商品的出库信息 */}
      {data.outbound && (
        <div className="bg-red-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3 flex items-center">
            <FaMinus className="mr-2 text-red-600" />
            出库信息
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">出库时间：</span>
              <span>{formatDate(data.outbound.created_at)}</span>
            </div>
            <div>
              <span className="text-gray-600">客户：</span>
              <span>{data.outbound.customer}</span>
            </div>
            <div>
              <span className="text-gray-600">销售价格：</span>
              <span>{formatCurrency(data.outbound.unit_price)}/瓶</span>
            </div>
            <div>
              <span className="text-gray-600">操作员：</span>
              <span>{data.outbound.operator}</span>
            </div>
            <div>
              <span className="text-gray-600">利润：</span>
              <span className="font-medium text-green-600">
                {formatCurrency(data.outbound.unit_price - data.inbound.unit_cost)}
              </span>
            </div>
            {data.outbound.notes && (
              <div className="col-span-2">
                <span className="text-gray-600">备注：</span>
                <span>{data.outbound.notes}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 状态说明 */}
      {data.unit.status === 'sold' && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center">
            <FaExclamationTriangle className="text-orange-500 mr-2" />
            <span className="text-orange-700 text-sm">
              此商品已于 <strong>{formatDate(data.unit.sold_at)}</strong> 出库给客户 <strong>{data.outbound?.customer}</strong>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// 箱码搜索结果组件 - 更新显示逻辑以支持已售商品
const BoxSearchResult = ({ data }) => {
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('zh-CN');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6">
      {/* 箱子基本信息 */}
      <div className={`${data.box.remaining_units > 0 ? 'bg-blue-50' : 'bg-gray-50'} rounded-lg p-4`}>
        <h3 className="text-lg font-semibold mb-3 flex items-center">
          <FaBox className={`mr-2 ${data.box.remaining_units > 0 ? 'text-blue-600' : 'text-gray-600'}`} />
          箱子信息
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">箱条码：</span>
            <span className="font-mono">{data.box.barcode || '无'}</span>
          </div>
          <div>
            <span className="text-gray-600">箱二维码：</span>
            <span className="font-mono">{data.box.qrcode || '无'}</span>
          </div>
          <div>
            <span className="text-gray-600">总瓶数：</span>
            <span>{data.box.units_count} 瓶</span>
          </div>
          <div>
            <span className="text-gray-600">剩余瓶数：</span>
            <span className={data.box.remaining_units > 0 ? 'text-green-600' : 'text-red-600'}>
              {data.box.remaining_units} 瓶
            </span>
          </div>
          <div>
            <span className="text-gray-600">箱状态：</span>
            <span className={`font-medium ${
              data.box.status === 'full' ? 'text-green-600' : 
              data.box.status === 'partial' ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {data.box.status === 'full' ? '完整' : 
               data.box.status === 'partial' ? '部分' : '空箱'}
            </span>
          </div>
        </div>
      </div>

      {/* 产品和入库信息 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-green-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3">产品信息</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-600">产品名称：</span>
              <span className="font-medium">{data.product.name}</span>
            </div>
            {data.product.chinese_name && (
              <div>
                <span className="text-gray-600">中文名称：</span>
                <span>{data.product.chinese_name}</span>
              </div>
            )}
            <div>
              <span className="text-gray-600">仓库：</span>
              <span>{data.warehouse.name}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3">入库信息</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-600">入库时间：</span>
              <span>{formatDate(data.inbound.date)}</span>
            </div>
            <div>
              <span className="text-gray-600">供应商：</span>
              <span>{data.inbound.supplier}</span>
            </div>
            <div>
              <span className="text-gray-600">入库成本：</span>
              <span>{formatCurrency(data.inbound.unit_cost)}/瓶</span>
            </div>
          </div>
        </div>
      </div>

      {/* 可售瓶子列表 */}
      {data.availableUnits && data.availableUnits.length > 0 && (
        <div className="bg-green-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3">
            可售瓶子 ({data.availableUnits.length} 瓶)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {data.availableUnits.map((unit) => (
              <div key={unit.id} className="bg-white p-2 rounded border">
                <div className="text-sm">
                  <div>第{unit.unit_index}瓶</div>
                  {unit.unit_barcode && (
                    <div className="font-mono text-xs text-gray-600">
                      {unit.unit_barcode.slice(-8)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 已售瓶子列表 - 更新显示更详细的出库信息 */}
      {data.soldUnits && data.soldUnits.length > 0 && (
        <div className="bg-red-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3">
            已售瓶子 ({data.soldUnits.length} 瓶)
          </h3>
          <div className="grid grid-cols-1 gap-2">
            {data.soldUnits.map((unit) => (
              <div key={unit.id} className="bg-white p-3 rounded border">
                <div className="flex justify-between items-center">
                  <div className="text-sm">
                    <div>第{unit.unit_index}瓶</div>
                    {unit.unit_barcode && (
                      <div className="font-mono text-xs text-gray-600">
                        {unit.unit_barcode}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-sm">
                    {unit.customer && <div className="text-gray-600">客户: {unit.customer}</div>}
                    {unit.unit_price && <div className="font-medium">{formatCurrency(unit.unit_price)}</div>}
                    {unit.outbound_date && <div className="text-xs text-gray-500">{formatDate(unit.outbound_date)}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 状态说明 */}
      {data.box.remaining_units === 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center">
            <FaExclamationTriangle className="text-orange-500 mr-2" />
            <span className="text-orange-700 text-sm">
              此箱已完全出库，共 <strong>{data.box.units_count}</strong> 瓶商品全部售出
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionHistoryinvPage;